import { useMemo } from 'react';
import type { ComponentContentSchema } from '@vibesites/runtime';
import {
  EditableCollectionBinding,
  useCollectionBinding,
  useTokens,
} from '@vibesites/runtime/react';
import type { Floorplan } from '@vibesites/schema';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { parseBedCount } from '../utils/bedroomOptions';
import { contrastRatio } from '../utils/luminance';
import { matchesPlanType, type PlanType } from '../utils/floorplanTypes';
import { useStaggerReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';

interface FloorplanShowcaseProps {
  tokenPrefix?: string;
  planType?: PlanType;
  ctaUrl?: string;
}

function toNumber(raw: unknown): number | null {
  if (raw == null || raw === '') return null;
  const n = parseFloat(String(raw).replace(/[^0-9.]/g, ''));
  return Number.isFinite(n) && n > 0 ? n : null;
}

function bedsLabel(plan: Floorplan): string {
  const beds = parseBedCount(plan.floorplan_beds_min);
  if (beds == null) return '';
  if (beds === 0) return 'Studio';
  return `${beds} Bed`;
}

function bathsLabel(plan: Floorplan): string {
  const baths = toNumber(plan.floorplan_baths_min);
  return baths ? `${baths} Bath` : '';
}

function sqftLabel(plan: Floorplan): string {
  const min = toNumber(plan.floorplan_sqft_min);
  const max = toNumber(plan.floorplan_sqft_max);
  if (!min && !max) return '';
  if (min && max && max !== min) return `${min.toLocaleString()} – ${max.toLocaleString()} Sq Ft`;
  return `${(min || max)!.toLocaleString()} Sq Ft`;
}

function priceLabel(plan: Floorplan): string {
  const min = toNumber(plan.floorplan_price_min);
  const max = toNumber(plan.floorplan_price_max);
  const start = min || max;
  return start ? `Starting at $${Math.round(start).toLocaleString()}` : 'Call for Pricing';
}

const BEDROOM_PARAMS = ['studio', 'one', 'two', 'three', 'four', 'five'];

/** Matches the `?bedroom=` tabs FloorplansList links to on the floor plans page. */
function availabilityUrl(plan: Floorplan, baseUrl: string): string {
  const beds = parseBedCount(plan.floorplan_beds_min);
  const param = beds == null ? null : BEDROOM_PARAMS[beds];
  return param ? `${baseUrl}?bedroom=${param}` : baseUrl;
}

function sizeSortKey(plan: Floorplan): number {
  return toNumber(plan.floorplan_sqft_min) ?? toNumber(plan.floorplan_sqft_max) ?? Infinity;
}

function availabilityLabel(plan: Floorplan): string {
  const units = toNumber(plan.floorplan_available_units);
  if (!units) return '';
  return `${units} Available`;
}

export function FloorplanShowcase({
  tokenPrefix,
  planType,
  ctaUrl = '/floor-plans',
}: FloorplanShowcaseProps) {
  const prefix = tokenPrefix || 'floorplanshowcase';
  const itemsPath = `${prefix}.items`;

  const allPlans = useCollectionBinding<Floorplan>(itemsPath, { collection: 'floorplans' });
  const plans = allPlans
    .filter((plan) => matchesPlanType(plan, planType))
    .sort((a, b) => sizeSortKey(a) - sizeSortKey(b));

  const tokens = useTokens();
  const ctaAccent = useMemo(() => {
    const primary = tokens?.color?.primary;
    const background = tokens?.color?.background || '#ffffff';
    if (!primary) return 'var(--vibe-color-primary)';
    return contrastRatio(primary, background) >= 4.5
      ? 'var(--vibe-color-primary)'
      : 'var(--vibe-color-text)';
  }, [tokens]);

  useRevealStyles();
  const gridRef = useStaggerReveal<HTMLDivElement>({ staggerMs: 100 });

  return (
    <section
      className="py-12 md:py-16 bg-[var(--vibe-color-background)]"
      style={{ ['--rise-fps-cta-accent' as string]: ctaAccent }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <EditableCollectionBinding bindingPath={itemsPath} label="Floor Plans">
          {plans.length === 0 ? (
            <div className="text-center py-8">
              <p className="font-body text-base md:text-lg text-[var(--vibe-color-text)] mb-6">
                No floor plans of this type are listed right now. Contact us for current availability.
              </p>
              <a href={ctaUrl} className="fps-btn">
                <span className="fps-btn-label">View All Floor Plans</span>
              </a>
            </div>
          ) : (
            <div ref={gridRef} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
              {plans.map((plan, i) => {
                const specs = [bedsLabel(plan), bathsLabel(plan), sqftLabel(plan)].filter(Boolean);
                const available = availabilityLabel(plan);
                const name = plan.floorplan_name || 'Floor Plan';
                return (
                  <article
                    key={plan.floorplan_id || i}
                    data-reveal
                    className="reveal-fade-up flex flex-col bg-white border border-black/10 rounded-[4px] overflow-hidden shadow-sm transition-shadow duration-300 hover:shadow-lg"
                  >
                    <div className="relative aspect-[4/3] bg-white p-4 border-b border-black/5">
                      {plan.floorplan_image_url ? (
                        <VibesitesImage
                          url={plan.floorplan_image_url}
                          alt={`${name} floor plan`}
                          sizes="(min-width: 1024px) 33vw, (min-width: 640px) 50vw, 100vw"
                          className="w-full h-full object-contain"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center bg-[var(--vibe-color-surface)] font-heading text-sm uppercase tracking-wider text-[var(--vibe-color-text)] opacity-60">
                          Floor plan coming soon
                        </div>
                      )}
                      {available && (
                        <span className="absolute top-3 left-3 px-3 py-1 font-heading text-[11px] uppercase tracking-wider bg-secondary text-secondary-foreground rounded-[2px]">
                          {available}
                        </span>
                      )}
                    </div>
                    <div className="flex flex-col flex-1 p-5 md:p-6 text-center">
                      <h3 className="font-heading text-xl font-bold uppercase tracking-wider text-[var(--vibe-color-text)]">
                        {name}
                      </h3>
                      {specs.length > 0 && (
                        <p className="font-body text-sm md:text-base text-[var(--vibe-color-text)] opacity-80 mt-2">
                          {specs.join(' | ')}
                        </p>
                      )}
                      <p className="font-heading text-base md:text-lg font-bold mt-3 text-[var(--rise-fps-cta-accent,var(--vibe-color-primary))]">
                        {priceLabel(plan)}
                      </p>
                      <div className="mt-auto pt-5">
                        <a href={availabilityUrl(plan, ctaUrl)} className="fps-btn">
                          <span className="fps-btn-label">Check Availability</span>
                        </a>
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>
          )}
        </EditableCollectionBinding>
      </div>

      {/* Schema-lint anchors — hidden, never interactive */}
      <EditableCollectionBinding
        bindingPath="floorplanshowcase.items"
        className="hidden"
        style={{ display: 'none' }}
      >
        <span />
      </EditableCollectionBinding>

      <style>{`
        /* Same "CTA over white" outline as ApplyNowActions / PropertyOverview:
           accent border + label at rest, fills with secondary on hover. */
        .fps-btn {
          position: relative;
          overflow: hidden;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          font-family: var(--vibe-font-body);
          font-size: 0.8rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.2em;
          padding: 0.875rem 2rem;
          border-radius: 2px;
          text-decoration: none;
          cursor: pointer;
          background: transparent;
          border: 1.5px solid var(--rise-fps-cta-accent, var(--vibe-color-primary));
          color: var(--rise-fps-cta-accent, var(--vibe-color-primary));
          box-shadow: none;
          transition: color 0.3s ease, transform 0.3s ease, box-shadow 0.3s ease, border-color 0.3s ease;
        }
        .fps-btn::before {
          content: '';
          position: absolute;
          inset: 0;
          background: var(--vibe-color-secondary);
          opacity: 0;
          transition: opacity 0.3s ease;
          z-index: 0;
        }
        .fps-btn-label {
          position: relative;
          z-index: 1;
        }
        .fps-btn:hover {
          color: var(--vibe-color-text-on-secondary);
          border-color: var(--vibe-color-secondary);
          transform: translateY(-2px);
          box-shadow: 0 4px 16px color-mix(in srgb, var(--vibe-color-secondary) 30%, transparent);
        }
        .fps-btn:hover::before { opacity: 1; }
        .fps-btn:active {
          transform: translateY(0);
          box-shadow: none;
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'FloorplanShowcase',
  displayName: 'Floor Plan Showcase',
  tokens: {},
  collectionBindings: {
    'floorplanshowcase.items': {
      label: 'Floor Plans',
      collection: 'floorplans',
      description: 'Floor plans shown on this page, filtered by bedroom count or townhome.',
    },
  },
};

(FloorplanShowcase as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default FloorplanShowcase;
